import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import { Card, Input, Label } from 'reactstrap';
import { DatePicker } from 'antd';
import datePickerLocale from 'antd/es/date-picker/locale/vi_VN';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';

import SearchIcon from 'assets/images/search-icon.svg';
import Button from 'components/App/Button';
import AppSelectValidation from 'components/App/SelectValidation';
import DataTableHistoryCoin from 'components/App/DataTableHistoryCoin';
import Pagination from 'components/Pagination';
import { CUSTOMER_ACCOUNT_TYPE_LABELS, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { convertIsoDateToString, getParamsFormObject } from 'utils';
import cn from 'utils/classnames';

import styles from './HistoryCoin.module.scss';

const DEFAULT_LIMIT = 10;
const DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'D/M/YYYY HH:mm';

const BONUS_TYPE_OPTIONS = [
  { value: 'MEMBERSHIP', label: 'Loại thành viên' },
  { value: 'AFFILIATE', label: 'Tiếp thị liên kết' },
];

const DEFAULT_FILTERS = {
  q: '',
  bonusType: '',
  from: '',
  to: '',
};

const mapHistoryItem = (item) => ({
  id: item._id,
  receivedAt: convertIsoDateToString(item.createdAt, DISPLAY_DATE_FORMAT) || '—',
  accountId: item.user?.userId || '',
  userId: item.user?._id || '',
  accountName: item.user?.fullName || '',
  memberRank: item.user?.level || '',
  orderId: item.userBooking?.orderId || '',
  bookingId: item.userBooking?._id || '',
  refundTypeLabel: item.bonusType === 'MEMBERSHIP' ? `Tài khoản ${CUSTOMER_ACCOUNT_TYPE_LABELS[item.user?.level]}` : 'Tiếp thị liên kết',
  amount: item.amount,
});

function HistoryCoin(props) {
  const {
    historyCoinList,
    historyCoinTotal,
    isLoadingGetHistoryCoin,
    actionGetHistoryCoin,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const tableRef = useRef(null);

  const searchQueries = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      q: params.get('q') || '',
      bonusType: params.get('bonusType') || '',
      from: params.get('from') || '',
      to: params.get('to') || '',
      page: Number(params.get('page') || 1),
    };
  }, [location.search]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
      bonusType: searchQueries.bonusType,
      from: searchQueries.from,
      to: searchQueries.to,
    },
    onSubmit: (values) => {
      history.push({
        pathname: location.pathname,
        search: getParamsFormObject({
          tab: 'history',
          q: values.q?.trim() || undefined,
          bonusType: values.bonusType || undefined,
          from: values.from || undefined,
          to: values.to || undefined,
          page: 1,
        }),
      });
    },
  });

  const fetchHistoryCoin = useCallback(() => {
    const payload = {
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
    };

    if (searchQueries.q) {
      payload.q = searchQueries.q;
    }

    if (searchQueries.bonusType) {
      payload.bonusType = searchQueries.bonusType;
    }

    if (searchQueries.from) {
      payload.from = searchQueries.from;
    }

    if (searchQueries.to) {
      payload.to = searchQueries.to;
    }

    actionGetHistoryCoin(payload);
  }, [actionGetHistoryCoin, searchQueries]);

  useEffect(() => {
    fetchHistoryCoin();
  }, [fetchHistoryCoin]);

  const histories = useMemo(
    () => (historyCoinList || []).map(mapHistoryItem),
    [historyCoinList],
  );

  const pageCount = useMemo(
    () => Math.ceil((historyCoinTotal || 0) / DEFAULT_LIMIT) || 0,
    [historyCoinTotal],
  );

  const dateRangeValue = useMemo(() => {
    if (!validation.values.from && !validation.values.to) {
      return null;
    }

    return [
      validation.values.from ? dayjs(validation.values.from, DATE_FORMAT) : null,
      validation.values.to ? dayjs(validation.values.to, DATE_FORMAT) : null,
    ];
  }, [validation.values.from, validation.values.to]);

  const isSearchBtnDisabled = useMemo(() => {
    const hasSearch = Boolean(validation.values.q?.trim());
    const hasFilter = Boolean(validation.values.bonusType);
    const hasDateRange = Boolean(validation.values.from || validation.values.to);

    return !hasSearch && !hasFilter && !hasDateRange;
  }, [
    validation.values.q,
    validation.values.bonusType,
    validation.values.from,
    validation.values.to,
  ]);

  const onChangeSearch = useCallback((event) => {
    validation.setFieldValue('q', event.target.value);
  }, [validation]);

  const onChangeBonusType = useCallback((option) => {
    validation.setFieldValue('bonusType', option?.value || '');
  }, [validation]);

  const onChangeDateRange = useCallback((dates) => {
    validation.setFieldValue('from', dates?.[0]?.format(DATE_FORMAT) || '', false);
    validation.setFieldValue('to', dates?.[1]?.format(DATE_FORMAT) || '', false);
  }, [validation]);

  const onResetForm = useCallback(() => {
    validation.resetForm({ values: DEFAULT_FILTERS });
    history.push({
      pathname: location.pathname,
      search: getParamsFormObject({
        tab: 'history',
        page: 1,
      }),
    });
  }, [history, location.pathname, validation]);

  const onChangePage = useCallback((selected) => {
    history.push({
      pathname: location.pathname,
      search: getParamsFormObject({
        tab: 'history',
        q: searchQueries.q || undefined,
        bonusType: searchQueries.bonusType || undefined,
        from: searchQueries.from || undefined,
        to: searchQueries.to || undefined,
        page: selected.selected + 1,
      }),
    });

    tableRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, location.pathname, searchQueries]);

  return (
    <>
      <Card className={cn('Section', styles.HistorySection)}>
        <form onSubmit={validation.handleSubmit}>
          <div className={styles.FilterRow}>
            <div className={styles.FilterSearch}>
              <Label className="Section__Search__Label">
                Tìm kiếm
              </Label>

              <div className="Section__Search__Container">
                <Input
                  placeholder="Tìm theo tên user..."
                  className="Section__Search__Input"
                  value={validation.values.q}
                  onChange={onChangeSearch}
                />

                <img
                  src={SearchIcon}
                  alt="search"
                  className="Section__Search__Icon__Search"
                />
              </div>
            </div>

            <div className={styles.FilterSelect}>
              <Label className="Section__Search__Label">
                Bộ lọc
              </Label>

              <AppSelectValidation
                placeholder="Chọn"
                name="bonusType"
                keyName="bonusType"
                options={BONUS_TYPE_OPTIONS}
                validation={validation}
                value={validation.values.bonusType}
                onChange={onChangeBonusType}
                isHiddenSearchable
                selectedClassName={styles.RefundTypeSelect}
              />
            </div>

            <div className={styles.FilterSelect}>
              <Label className="Section__Search__Label">
                Lọc theo ngày
              </Label>

              <DatePicker.RangePicker
                className={styles.FilterDatePicker}
                value={dateRangeValue}
                onChange={onChangeDateRange}
                format="DD/MM/YYYY"
                placeholder={['Từ ngày', 'Đến ngày']}
                locale={datePickerLocale}
                allowClear
              />
            </div>

            <div className={styles.FilterActions}>
              <Button
                outline
                classes="Section__Search__Button__ReType"
                color={themeColor.LIGHT}
                onClick={onResetForm}
              >
                Nhập lại
              </Button>

              <Button
                classes="Section__Search__Button__Search"
                type="submit"
                disabled={isSearchBtnDisabled}
              >
                Tìm kiếm
              </Button>
            </div>
          </div>
        </form>

        <div className={styles.TableWrapper} ref={tableRef}>
          <DataTableHistoryCoin
            histories={histories}
            isLoading={isLoadingGetHistoryCoin}
          />
        </div>
      </Card>

      {historyCoinTotal > 0 && (
        <Pagination
          page={searchQueries.page}
          pageCount={pageCount}
          onChangePage={onChangePage}
        />
      )}
    </>
  );
}

HistoryCoin.propTypes = {
  historyCoinList: PropTypes.instanceOf(Array),
  historyCoinTotal: PropTypes.number,
  isLoadingGetHistoryCoin: PropTypes.bool,
  actionGetHistoryCoin: PropTypes.func,
};

HistoryCoin.defaultProps = {
  historyCoinList: [],
  historyCoinTotal: 0,
  isLoadingGetHistoryCoin: false,
  actionGetHistoryCoin: INITIAL_VALUE.FUNCTION,
};

export default HistoryCoin;
